/**
 * ============================================================
 * KEJJA OS — OnboardingGate
 * Only redirect to /onboarding if we're SURE the user has no homes.
 * Uncertainty (error, no data) = allow through and let other layers handle.
 * ============================================================
 */

import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { useMyHomes } from '@/hooks/useMe';

export function OnboardingGate({ children }: { children: ReactNode }) {
  const { data, isLoading, isError } = useMyHomes();
  const location = useLocation();

  // Never gate on the onboarding pages themselves
  if (location.pathname.startsWith('/onboarding')) {
    return <>{children}</>;
  }

  // While loading, show a spinner (don't decide yet)
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-10 w-10 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
      </div>
    );
  }

  // On error (e.g. 401 or network), don't redirect to onboarding —
  // ProtectedRoute / AuthProvider will handle re-auth.
  if (isError) {
    return <>{children}</>;
  }

  // Only redirect when we SUCCESSFULLY fetched and the list is genuinely empty
  if (data && data.homes.length === 0) {
    return <Navigate to="/onboarding" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}
